'use client'

import { Select, SelectSearchableContent, SelectTrigger, SelectValue } from '@/components/ui/select'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { useDebouncedCallback } from '@/utilities/useDebounce'
import { cn } from '@/utilities/ui'
export type ClientOption = {
  partnerId: string
  partnerName: string
  canPlaceOrder: boolean
}

export default function ClientList({
  clientOptions,
  selected,
  loading,
  onSelect,
  defaultWidth = 'w-66',
  triggerClassName,
}: Readonly<{
  clientOptions: ClientOption[]
  selected: ClientOption | null
  loading?: boolean
  onSelect: (option: ClientOption) => void
  defaultWidth?: string
  triggerClassName?: string
}>) {
  const debouncedTrackSearch = useDebouncedCallback((query: string) => {
    trackEvent('client:partner_list:search', {
      query_length: query.length,
    })
  }, 300)

  return (
    <div className={cn('text-left', defaultWidth, 'xs:w-full')} id="clientList">
      <Select
        value={selected?.partnerId || ''}
        onValueChange={(val) => {
          const opt = clientOptions.find((o) => o.partnerId === val)
          if (opt) onSelect(opt)
        }}
      >
        <SelectTrigger
          className={cn(
            'h-10 text-white bg-cp-special-grey/10 relative items-center justify-between rounded-md',
            'px-4 py-2 text-base border-none shadow-sm w-full focus:outline-none',
            'focus:ring-0 focus:ring-offset-0 focus:border-none',
            triggerClassName,
          )}
          disabled={loading}
          data-testid="client-list-menu-button"
        >
          <SelectValue
            placeholder={
              loading && !selected ? 'Loading...' : selected?.partnerName || 'Select client'
            }
          />
        </SelectTrigger>
        <SelectSearchableContent
          options={clientOptions.map((option) => ({
            value: option.partnerId,
            label: option.partnerName,
          }))}
          selectedValue={selected?.partnerId}
          onSearchQueryChange={debouncedTrackSearch}
          searchPlaceholder="Search..."
          searchAriaLabel="Filter client list"
          noResults={
            <div className="px-3 py-2 text-sm" data-testid="client-list-no-results">
              No clients found
            </div>
          }
          itemClassName="cursor-pointer text-base pr-4 pl-8 py-2 focus:bg-gray-100"
          scrollUI="scrollbar"
          className="max-h-dropdown-menu bg-white border border-light-grey rounded-md shadow-lg **:data-radix-select-viewport:p-0"
        />
      </Select>
    </div>
  )
}
